import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Button, Checkbox, Menu, Modal, Toggle, DotsVertical, Plus } from '@opendate-app/setlist';

import client from '../../../shared/api/client';
import { errorText, toastError } from '../../../shared/toastHelper';
import CustomFieldDrawer from './CustomFieldDrawer';
import { fieldTypeIcon } from './FieldTypeTiles';
import OrderFormPreview from './OrderFormPreview';
import { askPerLabel, customFieldPayload, fieldMeta, flagValue, flagsFor, isBuiltIn, ticketTypeSummary } from './customFieldHelpers';
import './orderForm.css';

const DragHandle = ({ label, attributes, listeners }) => (
  <button type="button" className="order-form__handle" aria-label={label} {...attributes} {...listeners}>
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      {[4, 8, 12].map((cy) => [5, 11].map((cx) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.4" fill="currentColor" />))}
    </svg>
  </button>
);

DragHandle.propTypes = {
  label: PropTypes.string.isRequired,
  attributes: PropTypes.object.isRequired,
  listeners: PropTypes.object
};

DragHandle.defaultProps = {
  listeners: undefined
};

const SortableRow = ({ id, className, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };

  return (
    <div ref={setNodeRef} style={style} className={className}>
      {children({ attributes, listeners })}
    </div>
  );
};

SortableRow.propTypes = {
  id: PropTypes.number.isRequired,
  className: PropTypes.string.isRequired,
  children: PropTypes.func.isRequired
};

const TypeIcon = ({ type }) => {
  const Icon = fieldTypeIcon(type);
  return (
    <span className="order-form__icon" aria-hidden="true">
      <Icon width={16} height={16} />
    </span>
  );
};

TypeIcon.propTypes = {
  type: PropTypes.string.isRequired
};

const OrderFormFields = ({
  team, venueOwnership, customFieldsChanged, event, compact, venueSettingsUrl, venueName, header, onFieldsChange, showPreview, ticketTypes, askPerAvailable
}) => {
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [eventFields, setEventFields] = useState([]);
  const latest = useRef(venueOwnership);
  latest.current = venueOwnership;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const isEvent = Boolean(event);
  const askPer = isEvent || Boolean(askPerAvailable);
  const context = isEvent ? 'event' : 'venue';
  const flags = flagsFor(context);
  const baseUrl = isEvent
    ? `/teams/${team.id}/calendar_events/${event.id}/checkout_fields`
    : `/teams/${team.id}/crm/custom_fields`;
  const fields = [...((isEvent ? eventFields : venueOwnership.custom_fields) || [])].sort((a, b) => a.position - b.position);

  useEffect(() => {
    onFieldsChange(fields);
  }, [JSON.stringify(fields)]);

  const replaceFields = (customFields, venueOwnershipId = latest.current?.id) => (isEvent
    ? setEventFields(customFields)
    : customFieldsChanged(venueOwnershipId, customFields));

  const reload = async () => {
    if (isEvent) {
      const { data } = await client.get(`${baseUrl}.json`);
      setEventFields(data);
      return;
    }
    const venueOwnershipId = latest.current.id;
    const { data } = await client.get(`${baseUrl}.json`, {
      params: { custom_fieldable_type: 'VenueOwnership', custom_fieldable_id: venueOwnershipId }
    });
    replaceFields(data, venueOwnershipId);
  };

  const upsert = (saved) => {
    if (isEvent) return reload();
    const current = latest.current.custom_fields || [];
    return replaceFields(
      current.some((f) => f.id === saved.id) ? current.map((f) => (f.id === saved.id ? saved : f)) : [...current, saved]
    );
  };

  useEffect(() => {
    reload().catch((e) => toastError(errorText(e)));
  }, [isEvent ? event.id : venueOwnership.id]);

  const request = (id, body) => (id
    ? client.patch(`${baseUrl}/${id}.json`, { custom_field: body })
    : client.post(`${baseUrl}.json`, { custom_field: body }));

  const refreshAfterSave = (refresh) => {
    Promise.resolve()
      .then(refresh)
      .catch((e) => toastError(`Saved, but the field list couldn't refresh: ${errorText(e)}`));
  };

  const saveDraft = async (draft) => {
    const owner = isEvent ? null : { type: 'VenueOwnership', id: venueOwnership.id };
    const { data } = await request(draft.id, customFieldPayload(draft, owner, { withTicketTypes: isEvent, withAskPer: askPer }));
    setEditing(null);
    refreshAfterSave(() => upsert(data));
  };

  const destroy = async (field) => {
    await client.delete(`${baseUrl}/${field.id}.json`);
    refreshAfterSave(() => (isEvent
      ? reload()
      : replaceFields((latest.current.custom_fields || []).filter((f) => f.id !== field.id))));
  };

  const deleteDraft = async () => {
    await destroy(editing);
    setEditing(null);
  };

  const toggle = (field, key) => {
    request(field.id, { [key]: !flagValue(field, key) })
      .then(({ data }) => upsert(data))
      .catch((e) => toastError(errorText(e)));
  };

  const moveTo = (field, toIndex) => {
    const position = isEvent ? toIndex + 1 : fields[toIndex].position;
    replaceFields(arrayMove(fields, fields.indexOf(field), toIndex).map((f, i) => ({ ...f, position: i + 1 })));
    request(field.id, { position })
      .catch((e) => toastError(errorText(e)))
      .then(reload)
      .catch((e) => toastError(errorText(e)));
  };

  const onDragEnd = ({ active, over }) => {
    if (!over) return;
    if (active.id === over.id) return;
    moveTo(fields.find((f) => f.id === active.id), fields.findIndex((f) => f.id === over.id));
  };

  const resetToVenue = (field) => {
    client.post(`${baseUrl}/${field.id}/reset.json`)
      .then(reload)
      .catch((e) => toastError(errorText(e)));
  };

  const menuItems = (field, index) => (isEvent
    ? [
      { label: field.event_only ? 'Edit field' : 'Edit for this event', onClick: () => setEditing(field) },
      field.edited && { label: 'Reset to venue default', onClick: () => resetToVenue(field) },
      field.event_only && { label: 'Delete', onClick: () => setDeleting(field) }
    ]
    : [
      { label: 'Edit field', onClick: () => setEditing(field) },
      index > 0 && { label: 'Move up', onClick: () => moveTo(field, index - 1) },
      index < fields.length - 1 && { label: 'Move down', onClick: () => moveTo(field, index + 1) }
    ]).filter(Boolean);

  const badges = (field) => (
    <>
      {!isEvent && isBuiltIn(field) && <Badge size="sm" color="gray">Built-in</Badge>}
      {field.event_only && <Badge size="sm" color="brand">This event</Badge>}
      {field.edited && <Badge size="sm" color="warning">Edited</Badge>}
    </>
  );

  const meta = (field) => (askPer ? `${askPerLabel(field)} · ${fieldMeta(field)}` : fieldMeta(field));

  const ticketTypeCell = (field) => {
    if (!field.ticket_type_targeted) return <span className="order-form__meta-text">All tickets</span>;
    const names = ticketTypes.filter((tt) => (field.ticket_type_ids || []).map(Number).includes(Number(tt.id))).map((tt) => tt.name);
    if (!names.length) return <span className="order-form__chips"><Badge size="sm" color="warning">No ticket types</Badge></span>;
    return (
      <span className="order-form__chips">
        {names.map((name) => <Badge key={name} size="sm" color="gray">{name}</Badge>)}
      </span>
    );
  };

  const actions = (field, index, size = 'sm') => (
    <Menu
      placement="bottom-end"
      items={menuItems(field, index)}
      trigger={<Button variant="tertiary" size={size} icon={DotsVertical} aria-label={`Actions for ${field.name}`} />}
    />
  );

  const sortable = (rows) => (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
        {rows}
      </SortableContext>
    </DndContext>
  );

  const drawer = editing && (
    <CustomFieldDrawer
      customField={editing.id ? editing : null}
      onSave={saveDraft}
      onDelete={deleteDraft}
      onClose={() => setEditing(null)}
      context={context}
      venueSettingsUrl={venueSettingsUrl}
      venueName={venueName}
      eventName={event?.title || event?.name || null}
      ticketTypes={ticketTypes}
      askPerAvailable={askPer}
    />
  );

  const deleteModal = (
    <Modal
      open={Boolean(deleting)}
      onClose={() => setDeleting(null)}
      type="error"
      title="Delete this field?"
      confirmLabel="Delete field"
      onConfirm={() => {
        const field = deleting;
        setDeleting(null);
        destroy(field).catch((e) => toastError(errorText(e)));
      }}
    >
      <p className="mb-0">{deleting && `"${deleting.name}" will be removed from this event's checkout.`}</p>
    </Modal>
  );

  const addLink = (
    <div className="order-form__add">
      <Button variant="link-color" size="sm" icon={Plus} onClick={() => setEditing({})}>
        {isEvent ? 'Add a field for this event only' : 'Add a custom field'}
      </Button>
    </div>
  );

  if (compact) {
    return (
      <div className="order-form order-form--compact">
        {header}
        {sortable(fields.map((field, index) => (
          <SortableRow key={field.id} id={field.id} className={`order-form__card${field.enabled ? '' : ' order-form__card--off'}`}>
            {({ attributes, listeners }) => (
              <>
                <DragHandle label={`Reorder ${field.name}`} attributes={attributes} listeners={listeners} />
                <TypeIcon type={field.type} />
                <div className="order-form__name-cell">
                  <button type="button" className="order-form__name" title={field.name} onClick={() => setEditing(field)}>{field.name}</button>
                  <div className="order-form__meta">
                    {badges(field)}
                    <span className="order-form__meta-text">{`${askPerLabel(field)} · ${ticketTypeSummary(field)}`}</span>
                  </div>
                </div>
                <Toggle size="sm" aria-label={`Enabled: ${field.name}`} checked={flagValue(field, 'enabled')} onChange={() => toggle(field, 'enabled')} />
                {actions(field, index, 'xs')}
              </>
            )}
          </SortableRow>
        )))}
        {addLink}
        {drawer}
        {deleteModal}
      </div>
    );
  }

  const columns = `28px 36px minmax(0, 1fr) ${isEvent ? 'minmax(0, 160px) ' : ''}repeat(${flags.length}, 84px) 40px`;

  return (
    <div className="order-form">
      <div className="order-form__header">
        <div className="order-form__heading">{header}</div>
        <Button variant="secondary" size="sm" icon={Plus} onClick={() => setEditing({})}>
          {isEvent ? 'Add event field' : 'Add field'}
        </Button>
      </div>
      <div className={showPreview ? 'order-form__layout order-form__layout--preview' : 'order-form__layout'}>
        <div className="order-form__table">
          <div className="order-form__grid order-form__grid--head" style={{ gridTemplateColumns: columns }}>
            <span />
            <span />
            <span>Field</span>
            {isEvent && <span>Ticket types</span>}
            {flags.map((f) => <span key={f.key} className="order-form__center">{f.label}</span>)}
            <span className="sr-only">Actions</span>
          </div>
          {sortable(fields.map((field, index) => (
            <SortableRow key={field.id} id={field.id} className={`order-form__row${field.enabled ? '' : ' order-form__row--off'}`}>
              {({ attributes, listeners }) => (
                <div className="order-form__grid" style={{ gridTemplateColumns: columns }}>
                  <DragHandle label={`Reorder ${field.name}`} attributes={attributes} listeners={listeners} />
                  <TypeIcon type={field.type} />
                  <div className="order-form__name-cell">
                    <div className="order-form__title-line">
                      <button type="button" className="order-form__name" onClick={() => setEditing(field)}>{field.name}</button>
                      {badges(field)}
                    </div>
                    <div className="order-form__meta" title={meta(field)}>{meta(field)}</div>
                  </div>
                  {isEvent && ticketTypeCell(field)}
                  {flags.map((f) => (
                    <span key={f.key} className="order-form__center">
                      <Checkbox
                        aria-label={`${f.label}: ${field.name}`}
                        checked={flagValue(field, f.key)}
                        onChange={() => toggle(field, f.key)}
                      />
                    </span>
                  ))}
                  {actions(field, index)}
                </div>
              )}
            </SortableRow>
          )))}
          {addLink}
        </div>
        {showPreview && (
          <OrderFormPreview fields={fields} ticketTypes={ticketTypes} subtitle={isEvent ? 'Sample order' : 'Enabled, fan-visible fields'} />
        )}
      </div>
      {drawer}
      {deleteModal}
    </div>
  );
};

OrderFormFields.propTypes = {
  team: PropTypes.object.isRequired,
  askPerAvailable: PropTypes.bool,
  venueOwnership: PropTypes.object,
  customFieldsChanged: PropTypes.func,
  event: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string, name: PropTypes.string }),
  compact: PropTypes.bool,
  venueSettingsUrl: PropTypes.string,
  venueName: PropTypes.string,
  header: PropTypes.node,
  onFieldsChange: PropTypes.func,
  showPreview: PropTypes.bool,
  ticketTypes: PropTypes.arrayOf(PropTypes.object)
};

OrderFormFields.defaultProps = {
  venueOwnership: null,
  customFieldsChanged: () => {},
  event: null,
  compact: false,
  venueSettingsUrl: null,
  venueName: null,
  header: null,
  onFieldsChange: () => {},
  showPreview: true,
  ticketTypes: []
};

export default OrderFormFields;
